import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';

/**
 * Cloud Redirect/Landing Page
 *
 * When cloud infrastructure support is enabled (VITE_CLOUD_ENABLED=true and VITE_CLOUD_URL set),
 * redirects to the Cloud subdomain. Otherwise shows "Coming Soon" / informational page.
 */

const Cloud: React.FC = () => {
  const [countdown, setCountdown] = useState(5);
  const cloudEnabled = import.meta.env.VITE_CLOUD_ENABLED === 'true';
  const cloudUrl = import.meta.env.VITE_CLOUD_URL;
  const isRedirecting = cloudEnabled && !!cloudUrl;

  useEffect(() => {
    if (!isRedirecting) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.location.href = cloudUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRedirecting, cloudUrl]);

  // If Cloud URL is set, show redirect countdown
  if (isRedirecting) {
    return (
      <section className="page-shell page-section-major animate-fade-in">
        <div className="page-inner">
        <motion.div
          className="bg-secondary-900/80 backdrop-blur-sm rounded-xl shadow-xl p-8 sm:p-12 border border-primary-700/50 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="w-24 h-24 sm:w-28 sm:h-28 bg-primary-900/50 rounded-full flex items-center justify-center mx-auto mb-6 p-4">
            <img src="/mono-faircorp-cloud.png" alt="" className="w-full h-full object-contain" />
          </div>
          
          <div className="flex items-center justify-center mb-4">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-green-400 font-medium">Live</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-primary-300 mb-4">
            Redirecting to FairCorp Cloud
          </h1>
          
          <p className="text-secondary-300 text-lg mb-6">
            You're being redirected to FairCorp Cloud...
          </p>
          
          <div className="text-6xl font-bold text-primary-400 mb-6">
            {countdown}
          </div>
          
          <p className="text-secondary-400 text-sm mb-4">
            If you're not redirected automatically,{' '}
            <a
              href={cloudUrl}
              className="text-primary-400 hover:text-primary-300 underline transition-colors"
            >
              click here
            </a>
          </p>
          
          <motion.div
            className="w-full bg-secondary-700 rounded-full h-2 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              className="bg-primary-500 h-full"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 5, ease: 'linear' }}
            />
          </motion.div>
        </motion.div>
        </div>
      </section>
    );
  }

  // If Cloud URL is not set, show informational page (deploying soon)
  return (
    <section className="page-shell page-section-major animate-fade-in">
        <div className="page-inner">
      <motion.div
        className="bg-secondary-900/80 backdrop-blur-sm rounded-xl shadow-xl p-8 sm:p-12 border border-primary-700/50"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="text-center mb-8">
          <div className="w-24 h-24 sm:w-28 sm:h-28 bg-primary-900/50 rounded-full flex items-center justify-center mx-auto mb-6 p-4">
            <img src="/mono-faircorp-cloud.png" alt="" className="w-full h-full object-contain" />
          </div>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-primary-300 mb-4">
            FairCorp Cloud
          </h1>
          
          <div className="flex items-center justify-center mb-6">
            <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-yellow-400 font-medium">Deploying Soon</span>
          </div>
        </div>

        <div className="space-y-6 text-secondary-300">
          <p className="text-lg leading-relaxed">
            <strong className="text-primary-400">FairCorp Cloud</strong> is our secure and affordable 
            cloud storage system with guaranteed file safety. Built with enterprise-grade security 
            and an easy-to-use interface, making your data accessible anywhere while ensuring 
            complete protection.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div className="bg-secondary-800/50 rounded-lg p-6 border border-secondary-700">
              <h3 className="text-xl font-semibold text-primary-400 mb-3">Secure File Storage</h3>
              <p className="text-secondary-400">
                Enterprise-grade encryption and access controls to keep your files safe and private.
              </p>
            </div>
            
            <div className="bg-secondary-800/50 rounded-lg p-6 border border-secondary-700">
              <h3 className="text-xl font-semibold text-primary-400 mb-3">Easy File Management</h3>
              <p className="text-secondary-400">
                Intuitive interface for uploading, organizing, and sharing your files effortlessly.
              </p>
            </div>
            
            <div className="bg-secondary-800/50 rounded-lg p-6 border border-secondary-700">
              <h3 className="text-xl font-semibold text-primary-400 mb-3">Access Anywhere</h3>
              <p className="text-secondary-400">
                Access your files from any device, anywhere in the world with our responsive web app.
              </p>
            </div>
            
            <div className="bg-secondary-800/50 rounded-lg p-6 border border-secondary-700">
              <h3 className="text-xl font-semibold text-primary-400 mb-3">Affordable Pricing</h3>
              <p className="text-secondary-400">
                Competitive storage plans designed for individuals and businesses of all sizes.
              </p>
            </div>
          </div>

          <div className="mt-8 p-6 bg-primary-900/20 border border-primary-700/50 rounded-lg">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-primary-400 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-primary-400 font-semibold mb-2">Deployment In Progress</h4>
                <p className="text-primary-200/80 text-sm">
                  FairCorp Cloud is being deployed to its dedicated subdomain and will be available soon. 
                  Sign up for early access to be notified when it launches.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Button to="/contact" size="lg">
            Get Early Access
          </Button>
        </div>
      </motion.div>
      </div>
    </section>
  );
};

export default Cloud;
